/* eslint-disable no-use-before-define */
import { FC, Fragment, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  FormHelperText,
  Group,
  IconCross,
  Input,
  List,
  Loader,
  Steps,
  Table,
  Typography
} from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';

import { HelpIcon, ResponsiveLoader } from 'components';
import { Accordion } from 'components/accordion/Accordion';
import { useAlert } from 'contexts/alert/useAlert';
import {
  useGetExistentAndNonExistentMappingsQuery,
  useGetTreatmentTypesWithoutLabelQuery,
  useRegisterNewArtifactContentParametersMutation
} from 'data/generated/graphql';
import { ContentParametersEditor } from 'features/content-parameter-settings/MappingView';
import { isValidRange, validate } from 'features/content-parameter-settings/utils';
import { useConvertedProperty } from 'hooks';
import useContentParametersCatalog from 'hooks/contentParametersMapping/useContentParametersCatalog';
import { isDuplicate } from 'utils/array';
import env from 'utils/env';
import { reportError } from 'utils/errorBoundary';
import { isValidSectionAllessehId, isValidTreatmentTypeKey } from 'utils/text';
import styles from './ContentParameterRegister.module.scss';
import { RegisterProvider, useRegisterContext } from './RegisterContext';
import { useRegisterState } from './useRegisterState';

const ContentParameterRegister: FC = () => {
  const currentProperty = useConvertedProperty();
  const { registerState, dispatch } = useRegisterState();

  return (
    <>
      <Breadcrumbs>
        <Breadcrumbs.Item
          componentEl={Link}
          to={`/${currentProperty}/site-config/mobile-and-app`}
          className={styles.propertyBreadcrumb}
        >
          {currentProperty}
        </Breadcrumbs.Item>
        <Breadcrumbs.Item componentEl={Link} to={`/${currentProperty}/site-config/mobile-and-app/content-presentation/new`}>
          Register section page
        </Breadcrumbs.Item>
      </Breadcrumbs>
      <Box margin={{ top: 'mlg', bottom: 'md' }}>
        <Group align="center" gap="none" margin={{ top: 'smd', bottom: 'smd' }}>
          <Steps active={registerState.stepIndex} complete={registerState.stepIndex - 1}>
            <Steps.Item>Add Allesseh IDs</Steps.Item>
            <Steps.Item>Define treatment types</Steps.Item>
            <Steps.Item>Define labels for new treatments</Steps.Item>
            <Steps.Item>See register plan</Steps.Item>
            <Steps.Item>Set mappings</Steps.Item>
          </Steps>
        </Group>
      </Box>
      <RegisterProvider registerState={registerState} dispatch={dispatch}>
        {registerState.stepIndex === 1 && <StepOne />}
        {registerState.stepIndex === 2 && <StepTwo />}
        {registerState.stepIndex === 3 && <StepThree />}
        {registerState.stepIndex === 4 && <StepFour />}
        {registerState.stepIndex === 5 && <StepFive />}
      </RegisterProvider>
    </>
  );
};

const StepOne = () => {
  const allessehIdPrefix = env.UCS_COLLECTION_PREFIX;
  const { registerState, dispatch, goToNextStep } = useRegisterContext();
  const [inputErrors, setInputErrors] = useState<string[]>([]);

  useEffect(() => {
    const errors = registerState.allessehIds.map((allessehId, index) => {
      if (allessehId === '') {
        return '';
      }

      if (!isValidSectionAllessehId(allessehId)) {
        return `Allesseh ID must be all uppercase and follow the format ${allessehIdPrefix}_{PUBLICATION}_{SECTION_NAME}`;
      }

      if (isDuplicate(registerState.allessehIds, allessehId, index)) {
        return 'Duplicate Allesseh ID';
      }

      return '';
    });

    setInputErrors(errors);
  }, [allessehIdPrefix, registerState.allessehIds]);

  const onAddAllessehId = () => {
    dispatch({
      type: 'SET_ALLESSEH_IDS',
      payload: {
        allessehIds: registerState.allessehIds.concat([''])
      }
    });
  };

  const onRemoveAllessehId = (index: number) => {
    const newAllessehIds = registerState.allessehIds.filter((_, i) => i !== index);
    dispatch({
      type: 'SET_ALLESSEH_IDS',
      payload: {
        allessehIds: newAllessehIds
      }
    });
  };

  return (
    <Box padding={{ all: 'lg' }}>
      <Group direction="column" gap="xs">
        <Typography variant="header3">Step 1: Add Allesseh IDs</Typography>
        <Typography variant="bodytext">Enter the Allesseh IDs of the section pages to register.</Typography>{' '}
        <Button tertiary onClick={onAddAllessehId}>
          Add Allesseh ID
        </Button>
        {registerState.allessehIds.map((allessehId, index) => (
          <Group fullWidth key={index} wrap={false} gap="sm" margin={{ top: 'sm' }} valign="baseline">
            <Group fullWidth direction="column" gap="xs">
              <Input
                type="text"
                name={`allesseh-id-${index}`}
                placeholder="Allesseh ID"
                value={allessehId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const newAllessehIds = [...registerState.allessehIds];
                  newAllessehIds[index] = e.target.value;
                  dispatch({
                    type: 'SET_ALLESSEH_IDS',
                    payload: {
                      allessehIds: newAllessehIds
                    }
                  });
                }}
                error={!!inputErrors[index]}
              />
              {inputErrors[index] && <FormHelperText error>{inputErrors[index]}.</FormHelperText>}
            </Group>
            <Button tertiary icon={IconCross} color="lava" onClick={() => onRemoveAllessehId(index)} />
          </Group>
        ))}
        <Group margin={{ top: 'lg' }} align="end" fullWidth>
          <Button
            primary
            onClick={goToNextStep}
            disabled={
              registerState.allessehIds.length === 0 ||
              registerState.allessehIds.some((id) => id === '') ||
              inputErrors.some((error) => error)
            }
          >
            Next
          </Button>
        </Group>
      </Group>
    </Box>
  );
};

const StepTwo = () => {
  const { registerState, dispatch, goToPreviousStep, goToNextStep } = useRegisterContext();
  const [inputErrors, setInputErrors] = useState<string[]>([]);

  useEffect(() => {
    const errors = registerState.treatmentTypes.map(({ treatmentType }, index) => {
      if (treatmentType === '') {
        return '';
      }

      if (!isValidTreatmentTypeKey(treatmentType)) {
        return 'Treatment type key must be all uppercase and can only contain underscores as separators.';
      }

      if (
        isDuplicate(
          registerState.treatmentTypes.map((tt) => tt.treatmentType),
          treatmentType,
          index
        )
      ) {
        return 'Duplicate treatment type key';
      }

      return '';
    });

    setInputErrors(errors);
  }, [registerState.treatmentTypes]);

  const onAddTreatmentType = () => {
    dispatch({
      type: 'SET_TREATMENT_TYPES',
      payload: {
        treatmentTypes: registerState.treatmentTypes.concat([{ treatmentType: '', label: '' }])
      }
    });
  };

  const onRemoveTreatmentType = (index: number) => {
    const newTreatmentTypes = registerState.treatmentTypes.filter((_, i) => i !== index);
    dispatch({
      type: 'SET_TREATMENT_TYPES',
      payload: {
        treatmentTypes: newTreatmentTypes
      }
    });
  };

  return (
    <Box padding={{ all: 'lg' }}>
      <Group direction="column" gap="xs">
        <Typography variant="header3">Step 2: Define Treatment Type Keys</Typography>
        <Typography variant="bodytext">Define the treatment type keys that the section pages are using.</Typography>
        <Button tertiary onClick={onAddTreatmentType}>
          Add treatment type key
        </Button>
        {registerState.treatmentTypes.map(({ treatmentType }, index) => (
          <Group fullWidth key={index} wrap={false} gap="sm" margin={{ top: 'sm' }} valign="baseline">
            <Group fullWidth direction="column" gap="xs">
              <Input
                type="text"
                name={`treatment-type-${index}`}
                placeholder="Treatment type key"
                value={treatmentType}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const newTreatmentTypes = [...registerState.treatmentTypes];
                  newTreatmentTypes[index] = { ...newTreatmentTypes[index], treatmentType: e.target.value };
                  dispatch({
                    type: 'SET_TREATMENT_TYPES',
                    payload: {
                      treatmentTypes: newTreatmentTypes
                    }
                  });
                }}
                error={!!inputErrors[index]}
              />
              {inputErrors[index] && <FormHelperText error>{inputErrors[index]}</FormHelperText>}
            </Group>
            <Button tertiary icon={IconCross} color="lava" onClick={() => onRemoveTreatmentType(index)} />
          </Group>
        ))}
        <Group margin={{ top: 'lg' }} align="space-between" fullWidth>
          <Button secondary onClick={goToPreviousStep}>
            Previous
          </Button>
          <Button
            primary
            onClick={goToNextStep}
            disabled={
              registerState.treatmentTypes.length === 0 ||
              registerState.treatmentTypes.some((tt) => tt.treatmentType === '') ||
              inputErrors.some((error) => error)
            }
          >
            Next
          </Button>
        </Group>
      </Group>
    </Box>
  );
};

const StepThree = () => {
  const currentProperty = useConvertedProperty();
  const { registerState, dispatch, goToPreviousStep, goToNextStep } = useRegisterContext();
  const { data: catalogData } = useContentParametersCatalog();
  const { data, isFetching, error } = useGetTreatmentTypesWithoutLabelQuery(
    {
      publicationKey: currentProperty!,
      treatmentTypes: registerState.treatmentTypes.map(({ treatmentType }) => treatmentType)
    },
    { enabled: !!currentProperty, staleTime: 60000 * 10 } // Cache for 10 minutes
  );

  const onNext = useCallback(() => {
    goToNextStep();
    dispatch({
      type: 'GENERATE_MAPPINGS',
      payload: {
        defaultParametersByTreatment: catalogData?.getResolvedContentParametersCatalog.treatments.map((t) => ({
          treatmentType: t.treatmentType,
          parameters: t.defaultParameters
        }))
      }
    });
  }, [dispatch, goToNextStep, catalogData]);

  useLayoutEffect(() => {
    if (data) {
      const hasNoNewTreatmentTypes = data.getTreatmentTypesWithoutLabel.length === 0;
      if (hasNoNewTreatmentTypes) {
        onNext();
      }

      dispatch({
        type: 'SET_SKIPPED_NEW_LABEL_STEP',
        payload: {
          skipped: hasNoNewTreatmentTypes
        }
      });
    }
  }, [data, dispatch, onNext]);

  useEffect(() => {
    if (error) {
      reportError('Error loading treatment types without label', error as Error);
    }
  }, [error]);

  const nonExistentTreatments = data?.getTreatmentTypesWithoutLabel ?? [];

  return (
    <Box padding={{ all: 'lg' }}>
      <Group direction="column" gap="xs">
        <Typography variant="header3">Step 3: Define Treatment Types Labels</Typography>
        <Typography variant="bodytext">
          Define the labels for the treatment types that have never been registered before.
        </Typography>
        {isFetching ? (
          <Group direction="column" align="center" gap="none" fullWidth>
            <ResponsiveLoader size="lg" />
            <Typography
              margin={{
                top: 'md'
              }}
              variant="bodytext"
            >
              Loading new treatment types...
            </Typography>
          </Group>
        ) : error ? (
          <>
            <Typography variant="bodytext">
              Error loading new treatment types. Try going back to the previous step and then come back to this step. If
              the error persists, contact support.
            </Typography>
          </>
        ) : (
          nonExistentTreatments.map((treatmentType, index) => {
            const ttConfigIndex = registerState.treatmentTypes.findIndex((t) => t.treatmentType === treatmentType);

            return (
              <Group fullWidth key={index} wrap={false} gap="sm" valign="center" margin={{ top: 'sm' }}>
                <Typography>•</Typography>
                <Typography>{treatmentType}</Typography>
                <Input
                  type="text"
                  name={`treatment-type-label-${index}`}
                  placeholder="Treatment type label"
                  value={registerState.treatmentTypes[ttConfigIndex]?.label || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newTreatmentTypes = [...registerState.treatmentTypes];
                    newTreatmentTypes[ttConfigIndex] = { ...newTreatmentTypes[ttConfigIndex], label: e.target.value };
                    dispatch({
                      type: 'SET_TREATMENT_TYPES',
                      payload: {
                        treatmentTypes: newTreatmentTypes
                      }
                    });
                  }}
                />
              </Group>
            );
          })
        )}
        <Group margin={{ top: 'lg' }} align="space-between" fullWidth>
          <Button secondary onClick={goToPreviousStep}>
            Previous
          </Button>
          <Button
            primary
            onClick={onNext}
            disabled={
              nonExistentTreatments.length === 0 ||
              registerState.treatmentTypes
                .filter((tt) => nonExistentTreatments.includes(tt.treatmentType))
                .some((tt) => tt.label === '')
            }
          >
            Next
          </Button>
        </Group>
      </Group>
    </Box>
  );
};

const StepFour = () => {
  const currentProperty = useConvertedProperty();
  const { registerState, goToStep, goToPreviousStep, goToNextStep, skippedNewLabelStep } = useRegisterContext();
  const { data, isFetching, error } = useGetExistentAndNonExistentMappingsQuery(
    {
      associations: {
        allessehIds: registerState.allessehIds,
        treatmentTypes: registerState.treatmentTypes.map((t) => t.treatmentType)
      },
      publicationKey: currentProperty!
    },
    { enabled: !!currentProperty, staleTime: 60000 * 5 } // Cache for 5 minutes
  );
  const { data: catalogData, isFetching: configIsFetching, error: configError } = useContentParametersCatalog();

  useEffect(() => {
    if (configError) {
      reportError(
        `Error loading content parameters config for property ${currentProperty} in Step 4 of Register action`,
        configError as Error
      );
    }
  }, [currentProperty, configError]);

  const onPrevious = () => {
    if (skippedNewLabelStep) {
      goToStep(2);
    } else {
      goToPreviousStep();
    }
  };

  const treatmentLabels: Record<string, string> | undefined = catalogData
    ? Object.fromEntries(
        catalogData.getResolvedContentParametersCatalog.treatments.map((t) => [t.treatmentType, t.label])
      )
    : undefined;
  const allLabels = registerState.treatmentTypes.reduce((acc, curr) => {
    if (!acc[curr.treatmentType]) {
      acc[curr.treatmentType] = curr.label;
    }
    return acc;
  }, treatmentLabels ?? {});
  const hasActionsForNonExistentMappings =
    data &&
    Object.values(data.getExistentAndNonExistentMappings.nonExistentMappings as Record<string, string[]>).some(
      (arr) => arr.length > 0
    );
  const hasActionsForExistentMappings =
    data &&
    Object.values(data.getExistentAndNonExistentMappings.existentMappings as Record<string, string[]>).some(
      (arr) => arr.length > 0
    );

  return (
    <Box padding={{ all: 'lg' }}>
      <Typography variant="header3">Step 4: See Register Plan</Typography>
      <Typography variant="bodytext">Review what will be created before setting the mappings.</Typography>
      <Group direction="column" gap="sm">
        {isFetching || configIsFetching ? (
          <Group direction="column" align="center" gap="none" fullWidth>
            <ResponsiveLoader size="lg" />
            <Typography
              margin={{
                top: 'md'
              }}
              variant="bodytext"
            >
              Loading register plan...
            </Typography>
          </Group>
        ) : error ? (
          <>
            <Typography variant="bodytext">
              Error loading the register plan. Try going back to the previous step and then come back to this step. If
              the error persists, contact support.
            </Typography>
          </>
        ) : (
          <>
            {hasActionsForNonExistentMappings && (
              <Group direction="column" gap="xs">
                <Typography variant="header4" margin={{ top: 'md', bottom: 'sm' }}>
                  <Typography inline weight="bold" color="emerald">
                    Will create mappings
                  </Typography>{' '}
                  for the following section pages and treatment types:
                </Typography>
                {Object.entries(data.getExistentAndNonExistentMappings.nonExistentMappings as Record<string, string[]>)
                  .filter(([, treatmentTypes]) => treatmentTypes.length > 0)
                  .map(([allessehId, treatmentTypes]) => (
                    <>
                      <Typography weight="bold">• {allessehId}</Typography>
                      <List listStyle="bullet" style={{ paddingInlineStart: '32px' }}>
                        {treatmentTypes.map((tt) => (
                          <List.Item key={tt}>{allLabels[tt] ? `${allLabels[tt]} (${tt})` : tt}</List.Item>
                        ))}
                      </List>
                    </>
                  ))}
              </Group>
            )}
            {hasActionsForExistentMappings && (
              <Group direction="column" gap="xs">
                <Typography variant="header4" margin={{ top: 'md', bottom: 'sm' }}>
                  <Typography inline weight="bold" color="lava">
                    Will NOT create mappings
                  </Typography>{' '}
                  for the following section pages and treatment types, as they already exist:
                </Typography>
                {Object.entries(data.getExistentAndNonExistentMappings.existentMappings as Record<string, string[]>)
                  .filter(([, treatmentTypes]) => treatmentTypes.length > 0)
                  .map(([allessehId, treatmentTypes]) => (
                    <>
                      <Typography weight="bold">• {allessehId}</Typography>
                      <List listStyle="bullet" style={{ paddingInlineStart: '24px' }}>
                        {treatmentTypes.map((tt) => (
                          <List.Item key={tt}>{allLabels[tt] ? `${allLabels[tt]} (${tt})` : tt}</List.Item>
                        ))}
                      </List>
                    </>
                  ))}
              </Group>
            )}
          </>
        )}
      </Group>
      <Group margin={{ top: 'lg' }} align="space-between" fullWidth>
        <Button secondary onClick={onPrevious}>
          Previous
        </Button>
        <Button primary onClick={goToNextStep}>
          Next
        </Button>
      </Group>
    </Box>
  );
};

const StepFive = () => {
  const currentProperty = useConvertedProperty();
  const { registerState, dispatch, goToPreviousStep } = useRegisterContext();
  const { alertError, alertSuccess } = useAlert();
  const queryClient = useQueryClient();
  const { data, isFetching, error } = useContentParametersCatalog();
  const mutation = useRegisterNewArtifactContentParametersMutation();
  const navigate = useNavigate();

  useEffect(() => {
    if (error) {
      reportError(
        `Error loading content parameters config for property ${currentProperty} in Step 5 of Register action`,
        error as Error
      );
    }
  }, [currentProperty, error]);

  const handleSave = async () => {
    const errorsArr = registerState.mappings.map((mapping) => {
      const isValidSchema = validate(mapping.parameters);

      return {
        treatmentType: mapping.treatmentType,
        errors: isValidRange(mapping.parameters),
        schemaErrors: validate.errors,
        isValidSchema
      };
    });

    if (errorsArr.some(({ errors, isValidSchema }) => errors.length > 0 || !isValidSchema)) {
      const errorStrings = errorsArr.map(({ treatmentType, errors, schemaErrors }) => {
        const errorMessage =
          schemaErrors
            ?.map((error) => `${error.instancePath}${error.instancePath ? ': ' : ''}${error.message}`)
            .join('; ') ?? '';

        return `${treatmentType}: ${errors.join('; ')} ${errorMessage}`;
      });

      alertError(`There are errors in the following treatment types: ${errorStrings.join('\n')}`, {
        wrapNewlines: false,
        autoDismiss: false
      });

      return;
    }

    const mutationsResults = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const allessehId of registerState.allessehIds) {
      try {
        await mutation.mutateAsync({
          publicationKey: currentProperty!,
          contentParametersMappingRegisterNewArtifactInput: {
            allessehId,
            mappings: registerState.mappings
          }
        });
        mutationsResults.push(true);
      } catch (error) {
        mutationsResults.push(error);
      }
    }

    const allSuccessful = mutationsResults.every((result) => result === true);
    if (allSuccessful) {
      alertSuccess('New artifact mappings registered successfully.');
      void queryClient.invalidateQueries(['ResolvedContentParametersCatalog']);
      void queryClient.invalidateQueries(['GetTreatmentTypesWithoutLabel']);
      void queryClient.invalidateQueries(['GetExistentAndNonExistentMappings']);
      navigate(`/${currentProperty}/site-config/mobile-and-app`);
      return;
    }

    const failedMutations = mutationsResults.filter((result) => result !== true);
    if (failedMutations.length > 0) {
      const errorMessages = failedMutations.map((result) => {
        if (result instanceof Error) {
          return result.message;
        }
        return 'Unknown error';
      });

      alertError(`Error registering new artifact mappings:\n${errorMessages.join('\n')}`, {
        wrapNewlines: false,
        autoDismiss: false
      });
    }
  };

  const treatmentLabels: Record<string, string> | undefined = data
    ? Object.fromEntries(
        data.getResolvedContentParametersCatalog.treatments.map((t) => [t.treatmentType, t.label])
      )
    : undefined;

  return (
    <>
      <Box padding={{ all: 'lg' }} margin={{ bottom: 'lg' }}>
        <Typography variant="header3">Step 5: Set Mappings</Typography>
        <Typography variant="bodytext">Set the content presentation mappings for the treatment types.</Typography>
        <Group margin={{ top: 'lg' }} align="space-between" fullWidth>
          <Button secondary onClick={goToPreviousStep}>
            Previous
          </Button>
          <Button primary onClick={handleSave} disabled={mutation.isLoading}>
            {mutation.isLoading ? <Loader size="md" /> : 'Save'}
          </Button>
        </Group>
      </Box>
      <Table fullWidth>
        <Table.Body>
          <Table.Row>
            <Table.Cell align="center" valign="middle">
              <strong>Treatment Type</strong>
            </Table.Cell>
            <Table.Cell align="center" valign="middle">
              <strong>Key</strong>
            </Table.Cell>
          </Table.Row>
          {registerState.mappings.map((mapping) => (
            <Fragment key={mapping.treatmentType}>
              <Table.Row>
                <Table.Cell align="center" valign="middle">
                  {mapping.label ? (
                    mapping.label
                  ) : isFetching ? (
                    <ResponsiveLoader size="md" />
                  ) : (
                    (treatmentLabels?.[mapping.treatmentType] ?? '')
                  )}
                </Table.Cell>
                <Table.Cell align="center" valign="middle">
                  {mapping.treatmentType}
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell colSpan="2">
                  <Accordion TitleComponent={<Title />}>
                    <Group direction="column" gap="mlg">
                      {mapping.parameters.length > 0 && data && (
                        <Typography variant="bodytext" margin={{ bottom: 'sm' }}>
                          <strong>Note:</strong> These values are pre-populated from CONFIG defaults. You can modify
                          them as needed.
                        </Typography>
                      )}
                      <ContentParametersEditor>
                        <ContentParametersEditor.JSONEditor
                          dispatch={dispatch}
                          treatmentType={mapping.treatmentType}
                          parameters={mapping.parameters}
                        >
                          <HelpIcon
                            isLeftAnchored
                            text="Add the presentation mappings that describe how this treatment type should look like."
                          />
                        </ContentParametersEditor.JSONEditor>
                      </ContentParametersEditor>
                    </Group>
                  </Accordion>
                </Table.Cell>
              </Table.Row>
            </Fragment>
          ))}
        </Table.Body>
      </Table>
    </>
  );
};

interface TitleProps {}

const Title: FC<TitleProps> = () => (
  <Group align="space-between" fullWidth style={{ height: '24px' }}>
    <Group.Item>Click to Open</Group.Item>
  </Group>
);

export default ContentParameterRegister;
